import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router';
import api from '../utils/axiosConfig';

export const AuthContext = createContext(null);

// Turn an axios error from the login request into a user-facing message.
const getLoginErrorMessage = (error) => {
  if (error.code === 'ECONNABORTED') {
    return 'The request timed out. Please try again.';
  }
  if (!error.response) {
    return 'Unable to reach the server. Please check your connection and try again.';
  }

  const data = error.response.data;
  if (typeof data === 'string' && data.trim()) {
    return data;
  }
  if (data && typeof data === 'object') {
    // The backend login view returns {"error": "..."}; DRF uses "detail",
    // and serializer errors come back as {field: ["msg"]} or {non_field_errors: [...]}.
    const candidate =
      data.error || data.detail || data.message || data.non_field_errors;
    if (Array.isArray(candidate)) return candidate.join(' ');
    if (typeof candidate === 'string') return candidate;

    const firstField = Object.values(data).find(
      (v) => typeof v === 'string' || Array.isArray(v)
    );
    if (Array.isArray(firstField)) return firstField.join(' ');
    if (typeof firstField === 'string') return firstField;
  }

  if (error.response.status >= 500) {
    return 'The server encountered an error. Please try again later.';
  }
  return 'Login failed. Please check your credentials.';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      const userData = localStorage.getItem('user');
      const token = localStorage.getItem('accessToken');
      
      if (userData && token) {
        setUser(JSON.parse(userData));
        // Set authorization header for all subsequent requests
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      setLoading(false);
    };
    
    initializeAuth();
  }, []);

  // Listen for auth errors from the axios interceptor
  useEffect(() => {
    const handleAuthError = () => {
      logout();
      navigate('/login');
    };
    
    window.addEventListener('auth-error', handleAuthError);
    
    return () => {
      window.removeEventListener('auth-error', handleAuthError);
    };
  }, [navigate]);

  const login = async (email, password) => {
    // Note: do NOT toggle the provider-level `loading` state here. When it is
    // true the provider renders a "Loading..." placeholder instead of
    // `children`, which unmounts the Login page and wipes its local error
    // state, so failures were never shown to the user.
    try {
      // Drop any stale token so the login request is not rejected with 401
      // by the JWT authenticator before it reaches the login view.
      localStorage.removeItem('accessToken');
      delete api.defaults.headers.common['Authorization'];

      const response = await api.post('/login/', {
        email: email,
        password: password
      });

      const { access, refresh, user: userData } = response.data;

      localStorage.setItem('accessToken', access);
      localStorage.setItem('refreshToken', refresh);
      localStorage.setItem('user', JSON.stringify(userData));

      // Set authorization header for all subsequent requests
      api.defaults.headers.common['Authorization'] = `Bearer ${access}`;

      setUser(userData);

      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: getLoginErrorMessage(error) };
    }
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('currentOrganizationId');
    
    // Remove authorization header
    delete api.defaults.headers.common['Authorization'];
    
    setUser(null);
    navigate('/login');
  };

  const hasRole = (allowedRoles) => {
    if (!user) return false;
    return allowedRoles.includes(user.role);
  };

  const authContextValue = {
    user,
    loading,
    login,
    logout,
    hasRole,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {!loading ? children : <div className="flex h-screen items-center justify-center">Loading...</div>}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Protected route component
export const ProtectedRoute = ({ element, allowedRoles = [] }) => {
  const { user, loading, isAuthenticated, hasRole } = useAuth();

  if (loading) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length > 0 && !hasRole(allowedRoles)) {
    return <Navigate to="/" replace />;
  }

  return element;
};

export default AuthContext;